import React, { useEffect, useState, useMemo } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Table from "react-bootstrap/Table";
import Form from "react-bootstrap/Form";
import productAPI from "../../services/productapi";
import "../../static/css/catalogManagement.css";

const ProductStockModal = ({ invModal, productId, change }) => {
    const [productModal, setProductModal] = useState(invModal);
    const [productDetails, setProductDetails] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [sortConfig, setSortConfig] = useState({ key: 'branch', direction: 'asc' });
    const [saveMessage, setSaveMessage] = useState("");
    const [saving, setSaving] = useState(false);
    const [loadError, setLoadError] = useState("");

    const handleProductClose = () => {
        setProductModal(false);
        change();
    };

    const handleSearch = (event) => {
        const query = event.target.value;
        setSearchQuery(query);
        const filtered = productDetails.filter(item => {
            const branchName = item.branch?.branchName?.toLowerCase() || '';
            return branchName.includes(query.toLowerCase());
        });
        setFilteredData(filtered);
    };

    const sortData = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const sortedData = useMemo(() => {
        const dataToSort = [...filteredData];
        return dataToSort.sort((a, b) => {
            const { key, direction } = sortConfig;
            if (key === 'stock') return direction === 'asc' ? a.stock - b.stock : b.stock - a.stock;
            if (key === 'mrpPrice') return direction === 'asc' ? a.mrpPrice - b.mrpPrice : b.mrpPrice - a.mrpPrice;
            if (key === 'salePrice') return direction === 'asc' ? a.salePrice - b.salePrice : b.salePrice - a.salePrice;
            if (key === 'branch') {
                const aName = a.branch?.branchName?.toLowerCase() || '';
                const bName = b.branch?.branchName?.toLowerCase() || '';
                return direction === 'asc' ? aName.localeCompare(bName) : bName.localeCompare(aName);
            }
            return 0;
        });
    }, [filteredData, sortConfig]);

    const updateInventoryField = (itemId, field, value) => {
        const update = (items) => items.map((item) => item.inventoryId === itemId ? { ...item, [field]: value } : item);
        setFilteredData(update);
        setProductDetails(update);
        setSaveMessage("Unsaved change — leave the field to save.");
    };

    const saveInventoryField = async (item, field, value) => {
        const number = Number(value);
        if (value === "" || !Number.isFinite(number) || number < 0) {
            setSaveMessage("Enter a valid non-negative value before saving.");
            return;
        }
        setSaving(true);
        try {
            const response = await productAPI.updateInventory({
                inventoryId: item.inventoryId, itemID: item.itemID, branchId: item.branchId, [field]: number
            });
            if (response?.success === false || response?.status === "FAIL") throw new Error(response.message || "Update failed");
            setSaveMessage("Saved successfully.");
        } catch (error) {
            setSaveMessage("Save failed. Please retry by leaving the field again.");
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        setIsLoading(true);
        setLoadError("");
        const fetchDetail = async () => {
            try {
                const data = await productAPI.getProductStockData(productId);
                if (data?.status === "FAIL" || data?.success === false) {
                    throw new Error(data.message || "Unable to load branch stock.");
                }
                if (data && Array.isArray(data.data)) {
                    setProductDetails(data.data);
                    setFilteredData(data.data);
                } else {
                    setProductDetails([]);
                    setFilteredData([]);
                }
            } catch (e) {
                console.error("Failed to fetch product details:", e);
                setLoadError(e.message || "Unable to load branch stock. Please reopen this editor to retry.");
            } finally {
                setIsLoading(false);
            }
        };
        fetchDetail();
    }, [productId]);

    const indCurrency = "₹";

    return (
        <Modal show={productModal} onHide={handleProductClose} size="xl" centered dialogClassName="catalog-modal">
            {isLoading ? (
                "Product detail is loading...."
            ) : (
                <>
                    <div id="productCapture">
                        <div className="bg-light p-4">
                            <h4 className="fw-bold">Branch Price & Stock</h4>
                            <p className="text-muted small mb-2">Search branches, edit a value, then leave the field to save it.</p>
                            <div role="status" className="small">{saving ? "Saving…" : saveMessage}</div>
                            {loadError && <div className="catalog-stock-error" role="alert">{loadError}</div>}
                            <input
                                type="text"
                                className="form-control mt-2"
                                placeholder="Search by branch name..."
                                value={searchQuery}
                                onChange={handleSearch}
                            />
                        </div>
                        <div className="p-4">
                            <Table striped bordered hover responsive>
                                <thead>
                                    <tr>
                                        <th onClick={() => sortData('branch')} style={{ cursor: 'pointer' }}>
                                            Branch {sortConfig.key === 'branch' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                                        </th>
                                        <th onClick={() => sortData('stock')} style={{ cursor: 'pointer' }}>
                                            Stock {sortConfig.key === 'stock' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                                        </th>
                                        <th onClick={() => sortData('mrpPrice')} style={{ cursor: 'pointer' }}>
                                            MRP {sortConfig.key === 'mrpPrice' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                                        </th>
                                        <th onClick={() => sortData('salePrice')} style={{ cursor: 'pointer' }}>
                                            Selling Price {sortConfig.key === 'salePrice' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                                        </th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedData.length > 0 ? (
                                        sortedData.map(item => (
                                            <tr key={item.inventoryId}>
                                                <td>{item.branch?.branchName}</td>
                                                <td>
                                                    <Form.Control
                                                        type="number"
                                                        min="0"
                                                        value={item.stock}
                                                        onChange={(e) => updateInventoryField(item.inventoryId, "stock", e.target.value)}
                                                        onBlur={(e) => saveInventoryField(item, "stock", e.target.value)}
                                                        disabled={saving}
                                                        aria-label={`Stock for ${item.branch?.branchName}`}
                                                    />
                                                </td>
                                                <td>
                                                    <Form.Control
                                                        type="number"
                                                        step="0.01"
                                                        value={item.mrpPrice}
                                                        onChange={(e) => updateInventoryField(item.inventoryId, "mrpPrice", e.target.value)}
                                                        onBlur={(e) => saveInventoryField(item, "mrpPrice", e.target.value)}
                                                        disabled={saving}
                                                        aria-label={`MRP for ${item.branch?.branchName}`}
                                                    />
                                                </td>
                                                <td>
                                                    <Form.Control
                                                        type="number"
                                                        step="0.01"
                                                        value={item.salePrice}
                                                        onChange={(e) => updateInventoryField(item.inventoryId, "salePrice", e.target.value)}
                                                        onBlur={(e) => saveInventoryField(item, "salePrice", e.target.value)}
                                                        disabled={saving}
                                                        aria-label={`Selling price for ${item.branch?.branchName}`}
                                                    />
                                                </td>
                                                <td>
                                                    <Form.Check
                                                        type="switch"
                                                        id={`switch-${item.inventoryId}`}
                                                        label={item.invStatus === 1 ? "Active" : "Inactive"}
                                                        checked={item.invStatus === 1}
                                                        onChange={(e) =>
                                                            { const value = e.target.checked ? 1 : 0; updateInventoryField(item.inventoryId, "invStatus", value); saveInventoryField(item, "invStatus", value); }
                                                        }
                                                    />
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="5">No stock details available</td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>
                        <div className="pb-4 px-4">
                            <Row>
                                <Col md={6}>
                                    <Button
                                        variant="danger"
                                        className="w-100"
                                        onClick={handleProductClose}
                                    >
                                        Close
                                    </Button>
                                </Col>
                            </Row>
                        </div>
                    </div>
                </>
            )}
        </Modal>
    );
};

export default ProductStockModal;
